/**
 * FormViewerModal Component
 * Full-screen modal for viewing PDF forms inline via iframe.
 * Supports downloading and handles loading/error states.
 * Follows the PdfViewerModal pattern from power reporting.
 */

import { useState, useCallback, useEffect, useMemo, memo } from 'react';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { OathButton, OathDualSpinner } from '../../shared/OathUI';
import { getFormPdf, downloadForm } from '../../../services/oathFormsService';
import { toast } from 'react-toastify';

/**
 * FormViewerModal
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close handler
 * @param {Object} form - The form object to view (needs formId, formName, fileType)
 * @param {number} agencyId - Agency ID for dynamic PDF filling
 */
const isMobileDevice = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const FormViewerModal = ({ isOpen, onClose, form, agencyId }) => {
  const isMobile = useMemo(() => isMobileDevice(), []);
  const [pdfData, setPdfData] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load PDF when modal opens with a new form
  useEffect(() => {
    if (!isOpen || !form?.formId) {
      setPdfData(null);
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setError(null);
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      setIsLoading(true);
      setError(null);
      setPdfData(null);
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }

      const result = await getFormPdf(form.formId, agencyId);

      if (cancelled) return;

      if (result.success && result.data?.formBytes) {
        setPdfData(result.data.formBytes);

        // Convert base64 to Blob URL for better interactive PDF support
        const byteCharacters = atob(result.data.formBytes);
        const byteNumbers = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([byteNumbers], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } else {
        setError(result.error || 'Failed to load PDF');
      }
      setIsLoading(false);
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, form?.formId, agencyId]);

  // Handle download
  const handleDownload = useCallback(async () => {
    if (!form?.formId) return;

    // If we already have the base64 data, convert and download directly
    if (pdfData) {
      try {
        const byteCharacters = atob(pdfData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = form.fileName || `${form.formName || 'form'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('PDF downloaded successfully');
        return;
      } catch (err) {
        // Fall through to API download
      }
    }

    // Otherwise, download via API
    const result = await downloadForm(form.formId);
    if (result.success) {
      const url = window.URL.createObjectURL(result.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName || form.fileName || `${form.formName || 'form'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } else {
      toast.error(result.error || 'Failed to download form');
    }
  }, [form, pdfData]);

  // Open PDF in a new browser tab (mobile native viewer)
  const handleOpenInBrowser = () => {
    if (blobUrl) window.open(blobUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
      />

      {/* Modal - near full screen for PDF viewing */}
      <div className="fixed inset-3 sm:inset-6 flex flex-col">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
            <div className="flex items-center gap-2.5 min-w-0">
              <DocumentTextIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                {form?.formName || 'Form Viewer'}
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <OathButton
                onClick={handleDownload}
                disabled={isLoading}
                icon={ArrowDownTrayIcon}
                size="sm"
              >
                <span className="hidden sm:inline">
                  {form?.fileType?.toUpperCase() === 'DPDF' ? 'Download Blank' : 'Download'}
                </span>
              </OathButton>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Fillable form info banner */}
          {form?.fileType?.toUpperCase() === 'DPDF' && !isLoading && blobUrl && (
            <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <PencilSquareIcon className="h-4 w-4 flex-shrink-0" />
              This form has fillable fields. Fill them in directly, then use the save button in the PDF toolbar to download your completed form.
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-900">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <OathDualSpinner size="lg" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading PDF...</p>
              </div>
            ) : blobUrl ? (
              isMobile ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 px-6">
                  <DocumentTextIcon className="h-20 w-20 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-700 dark:text-gray-300 font-medium text-center">
                    PDF preview is not supported on mobile devices
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-xs">
                    <button
                      onClick={handleOpenInBrowser}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      Open in Browser
                    </button>
                    <button
                      onClick={handleDownload}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                      Download PDF
                    </button>
                  </div>
                </div>
              ) : (
                <iframe
                  src={blobUrl}
                  title={`PDF: ${form?.formName || 'Form'}`}
                  className="w-full h-full border-none"
                />
              )
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <DocumentTextIcon className="h-16 w-16 text-gray-300 dark:text-gray-600" />
                <p className="text-red-500 dark:text-red-400 font-medium">
                  Failed to load PDF
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-xs">
                  {error}
                </p>
                <OathButton
                  onClick={handleDownload}
                  icon={ArrowDownTrayIcon}
                  variant="outline"
                  size="sm"
                >
                  Try downloading instead
                </OathButton>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-2">
                <DocumentTextIcon className="h-16 w-16 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No PDF data available
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(FormViewerModal);
